/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { css, jsx } from '@compiled/react';

import { useFetchSmartLinkData } from '../hooks/use-fetch-smart-link-data';
import { LinkEmbedCompact } from './link-embed-compact';
import { LinkEmbedFull } from './link-embed-full';
import type { OpenLinkButtonClickOpts } from './link-embed-shared';

interface LinkEmbedProps {
	onLinkClick?: (opts: OpenLinkButtonClickOpts) => void;
	url: string;
}

const containerRoot = css({
	width: '100%',
	containerType: 'inline-size',
	containerName: 'link-embed-container',
});

const showCompact = css({
	'content-visibility': 'visible',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-container-queries
	'@container link-embed-container (min-width: 481px)': {
		'content-visibility': 'hidden',
	},
});

const showFull = css({
	'content-visibility': 'hidden',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-container-queries
	'@container link-embed-container (min-width: 481px)': {
		'content-visibility': 'visible',
	},
});

export const LinkEmbed = ({ url, onLinkClick }: LinkEmbedProps): JSX.Element => {
	const { isLoading, isError, objectResolverData } = useFetchSmartLinkData({ urls: [url] });
	const data = objectResolverData?.[0];
	const title = data?.name || (isError ? url : '');
	const provider = data?.generator?.name || '';
	const iconUrl = data?.generator?.icon?.url || '';
	const generatorName = data?.generator?.name || '';
	const previewHref = data?.preview?.href || '';
	const modifiedBy = data?.attributedTo?.name || '';
	const updatedDate = data?.updated ? new Date(data.updated) : null;
	const secondsAgo = updatedDate ? Math.round((Date.now() - updatedDate.getTime()) / 1000) : 0;
	const isOlderThanWeek =
		updatedDate && Date.now() - updatedDate.getTime() > 1000 * 60 * 60 * 24 * 7;

	const variantProps = {
		url,
		title,
		provider,
		iconUrl,
		generatorName,
		previewHref,
		isLoading,
		isError,
		modifiedBy,
		updatedDate,
		secondsAgo,
		isOlderThanWeek,
		onLinkClick,
	};

	return (
		<div css={containerRoot}>
			<div css={showCompact}>
				<LinkEmbedCompact {...variantProps} />
			</div>
			<div css={showFull}>
				<LinkEmbedFull {...variantProps} />
			</div>
		</div>
	);
};
